import { Builder } from '@builder.io/react'
import CdnImage from 'components/util/CdnImage'
import { HeadingClass, HeadingType } from 'enums'
import React from 'react'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

enum ImageSide {
  Left = 'left',
  Right = 'right'
}

enum Alignment {
  Center = 'center',
  Left = 'left',
  Right = 'right'
}

interface Props {
  backgroundColor: string
  imageSide: ImageSide
  expertImage: string
  imageAltText: string
  headingTextOne: string
  headingTextTwo: string
  headingTextColor: string
  contentText: string
  headingType: HeadingType
  headingClass: HeadingClass
  textAlignment: Alignment
  smallerImage: string
}

const ExpertSection = ({
  backgroundColor,
  imageSide,
  expertImage = '',
  imageAltText = '',
  headingTextOne,
  headingTextTwo,
  headingTextColor,
  contentText,
  headingType,
  headingClass,
  textAlignment,
  smallerImage
}: Props) => {
  const getAlignmentClass = () => {
    if (textAlignment === Alignment.Right) {
      return 'text-right'
    }
    if (textAlignment === Alignment.Center) {
      return 'text-center'
    }
    return 'text-left'
  }

  const getContent = () => {
    if (imageSide === ImageSide.Left) {
      return (
        <>
          {/* Desktop view */}
          <div className="hidden lg:block">
            <div className="grid grid-flow-col grid-rows-3 gap-4">
              <div className="row-span-3">
                {' '}
                {expertImage && (
                  <CdnImage
                    className={`relative mx-auto h-auto w-auto object-cover ${smallerImage ? 'lg:w-36' : 'lg:w-44'}`}
                    url={expertImage}
                    alt={imageAltText}
                    steps={[120, 176]}
                    // renderPlaceholder={false}
                  />
                )}
              </div>
              <div className="col-span-2 flex flex-auto flex-col pl-10 pt-10 lg:flex-row lg:pl-0 lg:pt-0">
                <CustomHeading
                  classList="line-height-fix lg:pr-2 tracking-tight text-gray-900 text-left font-medium !font-sans !font-medium"
                  headingClass={headingClass}
                  headingType={headingType}
                  color={headingTextColor}
                >
                  {headingTextOne}
                </CustomHeading>

                <CustomHeading
                  classList="line-height-fix tracking-tight text-gray-900 text-left font-medium !font-sans !font-medium"
                  headingClass={headingClass}
                  headingType={headingType}
                  color={headingTextColor}
                >
                  {headingTextTwo}
                </CustomHeading>
              </div>
              <div className=" prose prose-indigo col-span-2 row-span-2 -mt-10 pl-10 text-gray-500 lg:-mt-8 lg:pl-0">
                <p
                  className={`enable-list-styles text-base ${getAlignmentClass()}`}
                  dangerouslySetInnerHTML={{ __html: contentText }}
                ></p>
              </div>
            </div>
          </div>

          {/* Mobile view */}
          <div className="block lg:hidden">
            <div className="mx-auto flex w-4/5 flex-row">
              <div className="relative pt-8">
                {expertImage && (
                  <CdnImage
                    className={`relative mx-auto ${smallerImage ? 'lg:w-36' : 'lg:w-44'}`}
                    url={expertImage}
                    alt={imageAltText}
                    steps={170}
                    // renderPlaceholder={false}
                  />
                )}
              </div>
              <div className=" flex flex-auto flex-col pl-10 pt-10 ">
                <CustomHeading
                  classList="line-height-fix tracking-tight text-gray-900 text-left font-medium !font-sans !font-medium"
                  headingClass={headingClass}
                  headingType={headingType}
                  color={headingTextColor}
                >
                  {headingTextOne}
                </CustomHeading>

                <CustomHeading
                  classList="line-height-fix tracking-tight text-gray-900 text-left font-medium !font-sans !font-medium"
                  headingClass={headingClass}
                  headingType={headingType}
                  color={headingTextColor}
                >
                  {headingTextTwo}
                </CustomHeading>
              </div>
            </div>
            <div className=" prose prose-indigo mx-auto w-4/5 text-gray-500 ">
              <p
                className={`enable-list-styles text-base ${getAlignmentClass()}`}
                dangerouslySetInnerHTML={{ __html: contentText }}
              ></p>
            </div>
          </div>
        </>
      )
    }

    return (
      <>
        {/* Desktop view */}
        <div className="hidden lg:block">
          <div className="grid grid-flow-col grid-rows-3 gap-4">
            <div className="col-span-2 flex flex-auto flex-col pr-12 lg:flex-row lg:pr-0">
              <CustomHeading
                classList="line-height-fix lg:pr-2 tracking-tight text-gray-900 lg:text-left font-medium text-right !font-sans !font-medium"
                headingClass={headingClass}
                headingType={headingType}
                color={headingTextColor}
              >
                {headingTextOne}
              </CustomHeading>
              <CustomHeading
                classList="line-height-fix tracking-tight text-gray-900 lg:text-left font-medium text-right !font-sans !font-medium"
                headingClass={headingClass}
                headingType={headingType}
                color={headingTextColor}
              >
                {headingTextTwo}
              </CustomHeading>
            </div>
            <div className=" prose prose-indigo col-span-2 row-span-2 -mt-4 pl-16 text-gray-500 lg:pl-0">
              <p
                className={`enable-list-styles text-base ${getAlignmentClass()}`}
                dangerouslySetInnerHTML={{ __html: contentText }}
              ></p>
            </div>
            <div className="row-span-3 pr-16 lg:pr-0">
              {expertImage && (
                <CdnImage
                  className={`relative mx-auto object-cover ${smallerImage ? 'lg:w-36' : 'lg:w-44'}`}
                  url={expertImage}
                  alt={imageAltText}
                  steps={[1080]}
                  // renderPlaceholder={false}
                />
              )}
            </div>
          </div>
        </div>
        {/* Mobile view */}
        <div className="block lg:hidden">
          <div className="mx-auto flex w-4/5 flex-row">
            <div className=" flex flex-auto flex-col pt-10 ">
              <CustomHeading
                classList="line-height-fix tracking-tight text-gray-900 text-left font-medium !font-sans !font-medium"
                headingClass={headingClass}
                headingType={headingType}
                color={headingTextColor}
              >
                {headingTextOne}
              </CustomHeading>

              <CustomHeading
                classList="line-height-fix tracking-tight text-gray-900 text-left font-medium !font-sans !font-medium"
                headingClass={headingClass}
                headingType={headingType}
                color={headingTextColor}
              >
                {headingTextTwo}
              </CustomHeading>
            </div>
            <div className="relative w-full pt-8">
              {expertImage && (
                <CdnImage
                  className={`relative mx-auto object-cover ${smallerImage ? 'lg:w-36' : 'lg:w-44'}`}
                  url={expertImage}
                  alt={imageAltText}
                  steps={120}
                  // renderPlaceholder={false}
                />
              )}
            </div>
          </div>
          <div className=" prose prose-indigo mx-auto w-4/5 text-gray-500 ">
            <p
              className={`enable-list-styles text-base ${getAlignmentClass()}`}
              dangerouslySetInnerHTML={{ __html: contentText }}
            ></p>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className="relative mx-auto -pt-4 flex w-full flex-col items-center justify-center bg-auto bg-no-repeat lg:py-8 lg:flex-row lg:bg-100%"
        style={{ backgroundColor: backgroundColor }}
      >
        {getContent()}
      </div>
    </>
  )
}

export default ExpertSection

Builder.registerComponent(ExpertSection, {
  name: 'ExpertSection',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'imageSide',
      type: 'string',
      enum: ['left', 'right'],
      defaultValue: 'left'
    },
    {
      name: 'expertImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'imageBackdropColor',
      type: 'color',
      defaultValue: 'cyan'
    },
    {
      name: 'smallerImage',
      type: 'boolean',
      defaultValue: false
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingTextOne',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextTwo',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'contentText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'textAlignment',
      type: 'string',
      enum: ['left', 'center', 'right'],
      defaultValue: 'left'
    }
  ]
})
